import React from 'react';
import styled from 'styled-components';
import { InputTag } from 'common';
import { Button, Input, Radio, Select, Tag, Checkbox, Cascader } from 'antd';
import { isEmpty, debounce, omit } from 'lodash';
import { sceneDataMap } from 'common/constant/resourceScene';
import V2TeachingResourceFields from './v2/teaching-resource-fields';

export const Line = styled.div<{ visible: boolean }>`
  min-height: 40px;
  display: ${props => (props.visible ? 'flex' : 'none')};
  align-items: center;
  margin-bottom: 12px;
`;

export const LineHead = styled.div<{ required?: boolean }>`
  font-weight: 500;
  color: rgba(63, 63, 63, 1);
  font-size: 14px;
  width: 64px;
  position: relative;
  flex: 0 0 auto;
  line-height: 32px;
  &:before {
    content: '*';
    color: red;
    position: absolute;
    top: 0;
    left: -10px;
    display: ${props => (props.required ? 'block' : 'none')};
  }
`;

const StyledSelect = styled(Select)<any>`
  width: 100%;
  & .ant-select-selection {
    border-color: ${props => (props.isError ? '#e82c1e' : '#d9d9d9')};
  }
`;

const ErrorMessage = styled.span<any>`
  width: 120px;
  height: 30px;
  line-height: 30px;
  color: #e82c1e;
  font-size: 12px;
  padding-left: 1em;
  display: ${props => props.display};
`;

interface Entry {
  uuid: string;
  value: string;
}

interface OptionEntry extends Entry {
  options: Entry[];
}

interface FilterFormProps extends React.Props<FilterForm> {
  useAs: 'form' | 'filter';
  type: 'image' | 'audio' | 'document' | 'video' | 'animation';
  data: {
    copyrights?: Entry[];
    accents?: OptionEntry[];
    characteristics?: OptionEntry[];
    sources?: OptionEntry[];
    scenes?: OptionEntry[];
    organizations?: any;
  };
  onFilter?: (filter: any) => void;
  selectedData?: any;
  usage?: 'common' | 'teaching_resource';
  style?: React.CSSProperties;
  useIn?: 'upload';
}

interface FilterFormState {
  /** 课程资源类型 */
  selectScene: string;
  /** 标签 */
  tags: string[];
  /** 描述 */
  desc: string;
  /** 口音 */
  selectAccent: string;
  selectCategory: string;
  selectCopyright: string;
  selectOrganization: string;
  isOrganizationError: boolean;
  isCopyRightError: boolean;
  isSceneError: boolean;
  searchOrganizationName: string;
  courseGroupList: Array<{ uuid: string; cnName: string }>;
  courseList: Array<{ uuid: string; cnName: string }>;
  selectedCourseGroup: string;
  selectedCourse: string;
  isReal: boolean;
  selectedUseScene: string;
  disableDownload?: boolean;
  courseCatalogueUuid?: string;
}

const EMPTY_OPTION: any = { options: [] };

export class FilterForm extends React.Component<FilterFormProps, FilterFormState> {
  public static getDerivedStateFromProps(nextProps: FilterFormProps, prevState: FilterFormState) {
    if (nextProps.selectedData) {
      const {
        accent,
        biz,
        category,
        characteristics,
        copyright,
        course,
        scene,
        isReal,
        organizationId,
        courseSeriesUuid,
        courseUuid,
      } = nextProps.selectedData;
      return {
        ...prevState,
        selectAccent: accent,
        sceneType: biz,
        claType: category,
        selectCategory: characteristics,
        selectCopyright: copyright,
        selectClass: course,
        selectScene: scene,
        isReal,
        selectOrganization: organizationId,
        selectedCourseGroup: courseSeriesUuid,
        selectedCourse: courseUuid,
        selectedUseScene: scene,
      };
    }
    return prevState;
  }

  constructor(props: FilterFormProps) {
    super(props);
    this.state = {
      selectScene: undefined,
      tags: [],
      desc: undefined,
      selectAccent: undefined,
      selectCategory: undefined,
      selectCopyright: 'ALO7',
      selectOrganization: undefined,
      searchOrganizationName: undefined,
      isOrganizationError: false,
      isCopyRightError: false,
      isSceneError: false,
      courseGroupList: [],
      courseList: [],
      selectedCourseGroup: undefined,
      selectedCourse: undefined,
      isReal: false,
      selectedUseScene: undefined,
    };
    this.searchOrganization = debounce(this.searchOrganization, 500);
  }

  public componentDidMount() {
    this.fetchCourseGroupList();
    if (this.state.selectedCourseGroup) {
      this.fetchCourseList(this.state.selectedCourseGroup);
    }
  }
  public shouldComponentUpdate(nextProps: FilterFormProps, nextState: FilterFormState) {
    const { selectedCourseGroup } = nextState;
    if (selectedCourseGroup !== this.state.selectedCourseGroup) {
      this.fetchCourseList(selectedCourseGroup);
    }

    return true;
  }

  public fetchCourseGroupList = () => {
    nx.$api.course_series({ subject: alo7Nav.subject, pageNo: '0' }).then((res: any) => {
      this.setState({ courseGroupList: res.result });
    });
  };

  public fetchCourseList = (courseSeriesUuid: string) => {
    nx.$api
      .course({ subject: alo7Nav.subject, pageSize: '1000', courseSeriesUuid })
      .then((res: any) => {
        this.setState({ courseList: res.result });
      });
  };

  get characteristics() {
    const foo =
      this.props.data.characteristics &&
      this.props.data.characteristics.find(
        (v: any) => v.uuid.toString().toLowerCase() === this.props.type
      );
    if (foo && foo.options) {
      return foo;
    } else {
      return EMPTY_OPTION;
    }
  }

  get accents() {
    const foo =
      this.props.data.accents &&
      this.props.data.accents.find((v: any) => v.uuid.toString().toLowerCase() === this.props.type);
    if (foo && foo.options) {
      return foo;
    } else {
      return EMPTY_OPTION;
    }
  }

  get scenes() {
    const foo =
      this.props.data.scenes &&
      this.props.data.scenes.find((v: any) => v.uuid.toString().toLowerCase() === this.props.type);
    if (foo && foo.options) {
      return foo;
    } else {
      return EMPTY_OPTION;
    }
  }

  public getFieldValue(skipCp = false) {
    if (nx.$root.isInternal) {
      if (
        skipCp &&
        !this.state.selectCopyright &&
        this.props.useAs === 'form' &&
        this.props.usage !== 'teaching_resource'
      ) {
        this.setState({
          isCopyRightError: true,
        });
        return null;
      }
      if (
        skipCp &&
        this.state.selectCopyright === 'ORGANIZATION' &&
        !this.state.selectOrganization &&
        this.props.useAs === 'form' &&
        this.props.usage !== 'teaching_resource'
      ) {
        this.setState({
          isOrganizationError: true,
        });
        return null;
      }
      if (skipCp && !this.state.selectScene && this.props.usage === 'teaching_resource') {
        this.setState({
          isSceneError: true,
        });
        return null;
      }
    }
    const data = {
      accent: this.state.selectAccent,
      characteristics: this.state.selectCategory,
      copyright: this.state.selectCopyright,
      organizationId: this.state.selectOrganization,
      scene: this.state.selectScene || this.state.selectedUseScene, // 课程资源中对应课程资源类型，资源中对应使用场景
      tags: this.state.tags,
      type: this.props.type.toUpperCase(),
      isReal: this.state.isReal,
      courseSeriesUuid: this.state.selectedCourseGroup,
      courseUuid: this.state.selectedCourse,
      disableDownload: this.state.disableDownload,
      courseCatalogueUuid: this.state.courseCatalogueUuid,
    };
    Object.keys(data).forEach(key => {
      if (data[key] === undefined) {
        delete data[key];
      }
      if (Array.isArray(data[key]) && data[key].length === 0) {
        delete data[key];
      }
    });
    if (this.props.usage === 'teaching_resource') delete data['isPublic'];
    return data;
  }

  // 搜索机构使用
  public searchOrganization = (value: string) => {
    this.setState({
      searchOrganizationName: value,
    });
  };

  public render() {
    const { data, type, useAs, onFilter = () => ({}), usage, useIn } = this.props;
    const {
      desc,
      tags,
      isCopyRightError,
      isSceneError,
      selectCopyright,
      selectScene,
      selectAccent,
      selectCategory,
      selectOrganization,
      isOrganizationError,
      courseGroupList,
      courseList,
      isReal,
      selectedCourseGroup,
      selectedCourse,
      selectedUseScene,
      disableDownload,
      courseCatalogueUuid,
    } = this.state;

    if (isEmpty(data)) {
      return null;
    }

    return (
      <div
        style={{
          width: '400px',
          marginLeft: useAs === 'form' ? '22px' : '12px',
          maxHeight: '495px',
          overflow: 'auto',
          paddingLeft: '10px',
          flex: '0 0 auto',
          ...this.props.style,
        }}>
        <Line
          visible={usage !== 'teaching_resource' && nx.$root.isInternal}
          style={{
            marginBottom: isCopyRightError ? '0' : '12px',
            alignItems: isCopyRightError ? 'flex-start' : 'center',
          }}>
          <LineHead required={useAs === 'form'}>版权</LineHead>
          <>
            <StyledSelect
              isError={isCopyRightError}
              allowClear={true}
              value={selectCopyright}
              placeholder={'选择版权'}
              onChange={(v: string, t: any) => {
                this.setState({ selectCopyright: v, isCopyRightError: false });
                onFilter({
                  ...this.getFieldValue(),
                  copyright: v,
                });
              }}>
              {(data.copyrights || []).map((v: any) => (
                <Select.Option key={v.uuid} value={v.uuid}>
                  {v.value}
                </Select.Option>
              ))}
            </StyledSelect>
            <ErrorMessage display={isCopyRightError ? 'inline' : 'none'}>请选择版权!</ErrorMessage>
          </>
        </Line>
        <Line
          visible={usage !== 'teaching_resource' && selectCopyright === 'ORGANIZATION'}
          style={{
            marginBottom: isOrganizationError ? '0' : '12px',
            alignItems: isOrganizationError ? 'flex-start' : 'center',
          }}>
          <LineHead required={useAs === 'form'}>机构</LineHead>
          <>
            <StyledSelect
              isError={isOrganizationError}
              allowClear={true}
              showSearch={true}
              optionFilterProp={'children'}
              value={selectOrganization}
              placeholder={'选择机构'}
              onSearch={(value: string) => this.searchOrganization(value)}
              onChange={(v: string, t: any) => {
                this.setState({
                  selectOrganization: v,
                  isOrganizationError: false,
                });
                onFilter({
                  ...this.getFieldValue(),
                  organizationId: v,
                });
              }}>
              {(data.organizations || []).map((v: any, index: number) => (
                <Select.Option
                  key={v.uuid}
                  value={v.uuid}
                  style={{
                    display:
                      index > 19 &&
                      (!this.state.searchOrganizationName ||
                        v.value.search(this.state.searchOrganizationName) === -1) &&
                      'none',
                  }}>
                  {v.value}
                </Select.Option>
              ))}
            </StyledSelect>
            <ErrorMessage display={isOrganizationError ? 'inline' : 'none'}>
              请选择机构!
            </ErrorMessage>
          </>
        </Line>
        <Line
          visible={usage === 'teaching_resource'}
          style={{
            marginBottom: isSceneError ? '0' : '12px',
            alignItems: isSceneError ? 'flex-start' : 'center',
          }}>
          <LineHead required={true}>类型</LineHead>
          <>
            <StyledSelect
              isError={isSceneError}
              allowClear={true}
              value={selectScene}
              placeholder={'选择课程资源类型'}
              onChange={(v: string, t: any) => {
                this.setState({ selectScene: v, isSceneError: false });
              }}>
              {this.scenes.options.map((v: any) => (
                <Select.Option key={v.uuid} value={v.uuid}>
                  {v.value}
                </Select.Option>
              ))}
            </StyledSelect>
            <ErrorMessage display={isSceneError ? 'inline' : 'none'}>请选择类型</ErrorMessage>
          </>
        </Line>
        <Line visible={usage !== 'teaching_resource' && type === 'audio'}>
          <LineHead>口音</LineHead>
          <Radio.Group
            value={selectAccent}
            onChange={e => {
              this.setState({ selectAccent: e.target.value });
              onFilter({
                ...this.getFieldValue(),
                accent: e.target.value,
              });
            }}>
            {this.accents.options.map((v: any) => (
              <Radio key={v.uuid} value={v.uuid}>
                {v.value}
              </Radio>
            ))}
          </Radio.Group>
        </Line>
        <Line visible={usage !== 'teaching_resource' && type === 'audio'}>
          <LineHead>特征</LineHead>
          <StyledSelect
            allowClear={true}
            value={selectCategory}
            placeholder={'选择特征'}
            onChange={(v: string, t: any) => {
              this.setState({ selectCategory: v });
              onFilter({
                ...this.getFieldValue(),
                characteristics: v,
              });
            }}>
            {this.characteristics.options.map((v: any) => (
              <Select.Option key={v.uuid} value={v.uuid}>
                {v.value}
              </Select.Option>
            ))}
          </StyledSelect>
        </Line>
        <Line visible={usage !== 'teaching_resource' && type === 'image'}>
          <LineHead>来源</LineHead>
          <Checkbox
            checked={isReal}
            onChange={e => {
              this.setState({ isReal: e.target.checked });
              onFilter({
                ...this.getFieldValue(),
                isReal: e.target.checked,
              });
            }}>
            是否是真实图片
          </Checkbox>
        </Line>
        <Line visible={useAs === 'filter'}>
          <LineHead>场景</LineHead>
          <StyledSelect
            value={selectedUseScene}
            placeholder="请选择场景"
            optionFilterProp="children"
            showSearch={true}
            allowClear={true}
            onChange={(value: string) => {
              this.setState({
                selectedUseScene: value,
              });
              onFilter({
                ...this.getFieldValue(),
                scene: value,
              });
            }}>
            {Object.values(sceneDataMap).map(
              (item: { value: string; label: string; user: string[] }) => {
                if (item.user.indexOf(type) !== -1) {
                  return (
                    <Select.Option value={item.value} key={item.value}>
                      {item.label}
                    </Select.Option>
                  );
                }
                return null;
              }
            )}
          </StyledSelect>
        </Line>
        <Line visible={useAs === 'filter' && useIn !== 'upload'}>
          <LineHead>课程系列</LineHead>
          <StyledSelect
            value={selectedCourseGroup}
            placeholder="请选择课程系列"
            optionFilterProp="children"
            showSearch={true}
            allowClear={true}
            onChange={(value: string) => {
              this.setState({
                selectedCourseGroup: value,
                selectedCourse: undefined,
              });
              onFilter({
                ...this.getFieldValue(),
                courseSeriesUuid: value,
                courseUuid: undefined,
              });
            }}>
            {courseGroupList.map(item => (
              <Select.Option key={item.uuid} value={item.uuid}>
                {item.cnName}
              </Select.Option>
            ))}
          </StyledSelect>
        </Line>
        <Line visible={useAs === 'filter' && useIn !== 'upload'}>
          <LineHead>课程</LineHead>
          <StyledSelect
            value={selectedCourse}
            placeholder="请选择课程"
            optionFilterProp="children"
            showSearch={true}
            allowClear={true}
            onChange={(value: string) => {
              this.setState({ selectedCourse: value });
              onFilter({
                ...this.getFieldValue(),
                courseUuid: value,
              });
            }}>
            {courseList.map(item => (
              <Select.Option key={item.uuid} value={item.uuid}>
                {item.cnName}
              </Select.Option>
            ))}
          </StyledSelect>
        </Line>
        <Line visible={useAs === 'form'} style={{ alignItems: 'flex-start' }}>
          <LineHead>标签</LineHead>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              marginTop: '5px',
            }}>
            {tags.map((tag, index) => (
              <Tag
                style={{ marginBottom: '4px' }}
                key={Math.random()
                  .toString(32)
                  .substring(2)}
                closable={true}
                onClose={() => {
                  const index = this.state.tags.findIndex(v => v === tag);
                  this.state.tags.splice(index, 1);
                  this.setState({
                    tags: this.state.tags,
                  });
                }}>
                {tag}
              </Tag>
            ))}
            <InputTag
              style={{ marginBottom: '4px' }}
              onAdd={(value: string) => {
                this.setState({ tags: tags.concat([value]) });
              }}
            />
          </div>
        </Line>

        <V2TeachingResourceFields
          usage={usage}
          value={{ disableDownload, courseCatalogueUuid }}
          onChange={e => this.setState(e)}
        />

        <Line
          visible={useAs === 'form'}
          style={{
            alignItems: 'flex-start',
            marginTop: '12px',
            display: 'none',
          }}>
          <LineHead>描述</LineHead>
          <Input.TextArea
            placeholder={'请输入描述内容'}
            autoSize={{ minRows: 3, maxRows: 5 }}
            value={desc}
            onInput={e => {
              this.setState({ desc: e.target['value'].substring(0, 100) });
            }}
          />
        </Line>
        <Line visible={useAs === 'filter'} style={{ justifyContent: 'flex-end' }}>
          <Button
            htmlType={'button'}
            type={'primary'}
            onClick={() => {
              onFilter({});
            }}>
            重置
          </Button>
        </Line>
      </div>
    );
  }
}
